import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { SummernoteOptions } from 'ngx-summernote/lib/summernote-options';
import { FileService } from '../services/file.service';

@Injectable()
export class SummernoteHelper {
    constructor(
        private _fileService: FileService,
        private _translateService: TranslateService
    ) {}

    public getSummernoteConfig(heightInPx = null, editormElement: Element = null, editormElementID = null): SummernoteOptions {
        return {
            height: (heightInPx ? heightInPx : 200),
            toolbar: [
                ['font', ['bold', 'italic', 'underline', 'strikethrough', 'clear']],
                ['para', ['ul', 'ol']],
                ['insert', ['link']]
            ],
            callbacks: {
                onPaste: (e) => {
                    const evt = e.originalEvent || e;
                    const clipboardData = evt.clipboardData;
                    if (!editormElement) {
                        editormElement = document.getElementById(editormElementID);
                    }
                    const editable = editormElement.parentElement.querySelector(`input + div.note-editor.note-frame .note-editable`);

                    if (!clipboardData) {
                        return;
                    }

                    if (clipboardData.files) {
                        this.startLoader(editable);
                        const files = clipboardData.files;
                        const promises: Array<Promise<string>> = [];
                        for (const file of clipboardData.files) {
                            promises.push(this._fileService.convertFileToBase64(file));
                        }

                        Promise.all(promises)
                            .then(base64Array => {
                                this.handleImageUpload(files)
                                    .then(urls => {
                                        for (let i = 0; i < base64Array.length; i++) {
                                            const imgEl: HTMLImageElement = editable.querySelector(`img[src="${base64Array[i]}"]`);
                                            imgEl.src = urls[i];
                                        }

                                        // refresh form value
                                        const html = ($(editormElement) as any).summernote('code');
                                        ($(editormElement) as any).summernote('code', html);
                                        this.stopLoader(editable);
                                    });
                            });
                    }
                }
            }
        };
    }

    public async handleImageUpload(files: File[]): Promise<string[]> {
        const urls = [];
        for (const file of files) {
            const compressed = await this.compressImage(file, 1200, 1200, 0.75);
            const url = await this.uploadImage(compressed, file.name);
            urls.push(url);
        }

        return urls;
    }

    public compressImage(file: File, maxW = 1200, maxH = 1200, quality = 0.75): Promise<Blob> {
        return new Promise((resolve, reject) => {
            const img = new Image();
            img.onload = () => {
                let {width, height} = img;
                const ratio = Math.min(maxW / width, maxH / height, 1);
                width = Math.round(width * ratio);
                height = Math.round(height * ratio);
                const canvas = document.createElement('canvas');
                canvas.width = width;
                canvas.height = height;
                const ctx = canvas.getContext('2d');
                ctx.drawImage(img, 0, 0, width, height);
                const type = 'image/webp';
                canvas.toBlob(
                    (blob) => blob ? resolve(blob) : reject(new Error('Canvas toBlob failed')),
                    type,
                    quality
                );
            };
            img.onerror = reject;
            img.src = URL.createObjectURL(file);
        });
    }

    public async uploadImage(blob: Blob, name: string): Promise<string> {
        const form = new FormData();
        form.append('file', blob, name.replace(/\.\w+$/, '.webp'));
        return await this._fileService.uploadFile(new File([blob], 'image'), 'summernote', 0, 'summernote_editor');
    }

    public startLoader(editable: Element): void {
        const submitButtonElement = editable.closest('form').querySelector('button.btn.btn-success');
        $(submitButtonElement).prop('disabled', true);

        const div = document.createElement('div');
        div.classList.add('text-center', 'my-1', 'summernote-description');
        editable.insertAdjacentElement('afterend', div);
        div.insertAdjacentHTML('beforeend', `<span><span class="mr-2">${this._translateService.instant('global.loading_image')}</span><span class="spinner-border spinner-border-sm" role="status" aria-hidden="true"></span></span>`);
    }

    public stopLoader(editable: Element): void {
        const submitButtonElement = editable.closest('form').querySelector('button.btn.btn-success');
        editable.nextSibling.remove();
        $(submitButtonElement).prop('disabled', false);
    }
}

